import React, { useEffect, useState } from 'react';
import api from '../services/api';
import TabelaCrud from '../components/TabelaCrud';
import './PaginaPublicaPage.css';

interface CrecheDados {
  id: number;
  nome: string;
  nome_fantasia?: string | null;
  slug?: string | null;
  logo_url?: string | null;
  site_url?: string | null;
  apresentacao_titulo?: string | null;
  apresentacao_texto?: string | null;
}

interface Plano {
  id: number;
  creche_id: number;
  nome: string;
  periodicidade: 'mensal' | 'semestral' | 'anual';
  valor_base: number;
  ativo: boolean;
}

const ORIGEM_PUBLICA = window.location.origin;

export default function PaginaPublicaPage() {
  const [creche, setCreche] = useState<CrecheDados | null>(null);
  const [form, setForm] = useState({ site_url: '', apresentacao_titulo: '', logo_url: '', apresentacao_texto: '' });
  const [buscandoSite, setBuscandoSite] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');

  useEffect(() => {
    api.get<CrecheDados[]>('/creches').then(({ data }) => {
      const minhaCreche = data?.[0] || null;
      setCreche(minhaCreche);
      setForm({
        site_url: minhaCreche?.site_url || '',
        apresentacao_titulo: minhaCreche?.apresentacao_titulo || '',
        logo_url: minhaCreche?.logo_url || '',
        apresentacao_texto: minhaCreche?.apresentacao_texto || '',
      });
    });
  }, []);

  const linkPublico = creche?.slug ? `${ORIGEM_PUBLICA}/c/${creche.slug}` : '';

  function onChange(campo: keyof typeof form, valor: string) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  async function buscarDadosDoSite() {
    if (!form.site_url.trim()) {
      setErro('Informe a URL do site da creche primeiro.');
      return;
    }
    setErro('');
    setBuscandoSite(true);
    try {
      const { data } = await api.get<{ titulo: string; imagem: string; descricao: string; erro?: string }>(
        '/creches/extrair-og',
        { params: { url: form.site_url.trim() } },
      );
      if (data.erro) {
        setErro(data.erro);
      }
      setForm((prev) => ({
        ...prev,
        apresentacao_titulo: data.titulo || prev.apresentacao_titulo,
        logo_url: data.imagem || prev.logo_url,
        apresentacao_texto: data.descricao || prev.apresentacao_texto,
      }));
    } catch (err: any) {
      setErro(err?.response?.data?.message || 'Não foi possível buscar dados do site.');
    } finally {
      setBuscandoSite(false);
    }
  }

  async function salvar() {
    if (!creche) return;
    setSalvando(true);
    setErro('');
    setSucesso('');
    try {
      await api.put(`/creches/${creche.id}`, {
        site_url: form.site_url.trim() || null,
        apresentacao_titulo: form.apresentacao_titulo.trim() || null,
        logo_url: form.logo_url.trim() || null,
        apresentacao_texto: form.apresentacao_texto.trim() || null,
      });
      setSucesso('Página pública atualizada com sucesso!');
      setTimeout(() => setSucesso(''), 3000);
    } catch {
      setErro('Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  function copiarLink() {
    if (!linkPublico) return;
    navigator.clipboard.writeText(linkPublico).then(() => {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    });
  }

  return (
    <div className="pagina-publica">
      <div className="pp-card">
        <h2>🌐 Página Pública da Creche</h2>
        <p className="pp-desc">
          Esta é a página que os pais veem para conhecer a creche, ver os planos de atendimento
          e se pré-inscrever para matrícula.
        </p>

        {linkPublico ? (
          <div className="pp-link-row">
            <input readOnly value={linkPublico} />
            <button onClick={copiarLink} className="btn-primario" type="button">
              {copiado ? '✓ Copiado!' : 'Copiar link'}
            </button>
            <a href={linkPublico} target="_blank" rel="noreferrer" className="btn-secundario">
              Visualizar
            </a>
          </div>
        ) : (
          <p>Carregando...</p>
        )}

        <p className="pp-section-title">Aproveitar dados do site da creche (opcional)</p>
        <label>Site institucional da creche
          <div className="pp-inline">
            <input
              value={form.site_url}
              onChange={(e) => onChange('site_url', e.target.value)}
              placeholder="https://www.suacreche.com.br"
            />
            <button onClick={buscarDadosDoSite} disabled={buscandoSite} className="btn-secundario" type="button">
              {buscandoSite ? 'Buscando...' : 'Buscar dados do site'}
            </button>
          </div>
        </label>
        <p className="pp-dica">
          Se o site informado tiver título/imagem/descrição configurados para redes sociais, eles
          serão sugeridos abaixo — sempre editáveis antes de salvar.
        </p>

        <p className="pp-section-title">Apresentação na Página Pública</p>
        <label>Título
          <input
            value={form.apresentacao_titulo}
            onChange={(e) => onChange('apresentacao_titulo', e.target.value)}
            placeholder={creche?.nome_fantasia || creche?.nome || 'Nome da creche'}
          />
        </label>
        <label>URL da imagem
          <input
            value={form.logo_url}
            onChange={(e) => onChange('logo_url', e.target.value)}
            placeholder="https://..."
          />
        </label>
        <label>Texto de apresentação
          <textarea
            rows={4}
            value={form.apresentacao_texto}
            onChange={(e) => onChange('apresentacao_texto', e.target.value)}
            placeholder="Conte brevemente sobre a creche: proposta pedagógica, diferenciais, estrutura..."
          />
        </label>

        {erro && <p className="pp-erro">{erro}</p>}
        {sucesso && <p className="pp-sucesso">{sucesso}</p>}
        <button onClick={salvar} disabled={salvando || !creche} className="btn-primario" type="button">
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      <TabelaCrud<Plano>
        titulo="Planos de Atendimento"
        endpoint="/financeiro/planos"
        colunas={[
          { chave: 'id', label: 'ID' },
          { chave: 'nome', label: 'Nome' },
          { chave: 'periodicidade', label: 'Periodicidade' },
          { chave: 'valor_base', label: 'Valor Base (R$)' },
          {
            chave: 'ativo',
            label: 'Ativo',
            render: (item) => (
              <span className={`badge badge-${item.ativo ? 'pago' : 'vencido'}`}>
                {item.ativo ? 'Sim' : 'Não'}
              </span>
            ),
          },
        ]}
        itemVazio={{ nome: '', periodicidade: 'mensal', valor_base: 0, ativo: true }}
        validateItem={(item) => {
          if (!item.nome?.trim()) return 'Nome do plano é obrigatório.';
          if (!item.valor_base || Number(item.valor_base) <= 0) return 'Valor base deve ser maior que zero.';
          return null;
        }}
        renderFormulario={(item, onChange) => (
          <>
            <label>Nome do Plano
              <input value={item.nome ?? ''} onChange={(e) => onChange('nome', e.target.value)} placeholder="Ex: Plano Integral" />
            </label>
            <label>Periodicidade
              <select value={item.periodicidade ?? 'mensal'} onChange={(e) => onChange('periodicidade', e.target.value)}>
                <option value="mensal">Mensal</option>
                <option value="semestral">Semestral</option>
                <option value="anual">Anual</option>
              </select>
            </label>
            <label>Valor Base (R$)
              <input type="number" step="0.01" value={item.valor_base ?? 0} onChange={(e) => onChange('valor_base', Number(e.target.value))} />
            </label>
            <label>
              <input type="checkbox" checked={item.ativo ?? true} onChange={(e) => onChange('ativo', e.target.checked)} />
              {' '}Exibir este plano na página pública
            </label>
          </>
        )}
      />
    </div>
  );
}
